import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Utility function to combine class names
const cn = (...classes: (string | undefined | null | false)[]): string => {
  return classes.filter(Boolean).join(' ');
};

export interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  activeIcon?: React.ReactNode;
  path: string;
  badge?: string | number;
}

export interface BottomNavigationProps {
  items: NavItem[];
  className?: string;
}

const BottomNavigation: React.FC<BottomNavigationProps> = ({ items, className }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const handleNavigation = (path: string) => {
    navigate(path);
  };
  
  return (
    <nav className={cn(
      "fixed bottom-0 left-0 right-0 z-50 bg-[#0F1114] border-t border-white/10 safe-area-inset-bottom lg:hidden",
      className
    )}>
      <div className="flex items-center justify-around h-16 px-2">
        {items.map((item) => {
          const isActive = location.pathname === item.path;
          
          return (
            <button
              key={item.id}
              onClick={() => handleNavigation(item.path)}
              className={cn(
                "flex flex-col items-center justify-center min-w-0 flex-1 px-2 py-1 transition-all duration-200 rounded-lg group",
                "hover:bg-white/5",
                isActive && "text-primary-500"
              )}
            >
              <div className="relative flex items-center justify-center">
                {/* Icon */}
                <div className={cn(
                  "transition-all duration-200",
                  isActive ? "scale-110" : "scale-100"
                )}>
                  {isActive && item.activeIcon ? item.activeIcon : item.icon}
                </div>
                
                {/* Badge */}
                {item.badge && (
                  <span className="absolute -top-1 -right-1 h-4 w-4 bg-error-500 text-white text-xs rounded-full flex items-center justify-center min-w-0">
                    {typeof item.badge === 'number' && item.badge > 9 ? '9+' : item.badge}
                  </span>
                )}
              </div>
              
              {/* Label */}
              <span className={cn(
                "text-xs font-medium mt-1 transition-all duration-200 truncate w-full text-center",
                isActive 
                  ? "text-primary-500" 
                  : "text-gray-400 group-hover:text-gray-200"
              )}>
                {item.label}
              </span>
              
              {/* Active indicator */}
              {isActive && (
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 bg-primary-500 rounded-full" />
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export { BottomNavigation };